import {
  apiWithAdminToken,
  apiWithMerchantToken,
  apiWithMerchantTokenV2,
  apiWithRefreshToken,
  imageApiWithAdminToken,
  imageApiWithMerchantToken,
} from '@lib/api';
import {
  apply,
  withAdminTokenAccepted,
  withMerchantTokenAccepted,
  withRefreshTokenAccepted,
  withTokenError,
  withTokenRejected,
  withUserTokenAccepted,
} from '@lib/interceptors';
import axios, { AxiosInstance } from 'axios';

/**
 * v2 API
 */

const API_URL_V2 = process.env.REACT_APP_API_URL_V2;
const API_IMG_URL = process.env.REACT_APP_API_IMG_URL;

const createInstance = (url?: string, timeout?: number) =>
  axios.create({ baseURL: `${API_URL_V2}${url}`, timeout: timeout || 30000 });
const createImageInstance = (url?: string, timeout?: number) =>
  axios.create({ baseURL: `${API_IMG_URL}${url}`, timeout: timeout || 0 });

const auth = createInstance(`/auth`);
const seat = createInstance(`/seat`);
const user = createInstance(`/user`);
const ticket = createInstance(`/ticket`);

const authWithRefreshToken = createInstance('/auth');
export const authWithUserToken = createInstance('/auth');
export const authWithMerchantToken = createInstance('/auth');
const authWithAdminToken = createInstance('/auth');
export const userWithoutToken = createInstance('/user');

export const userWithAdminToken = createInstance('/admin/user');
export const couponWithAdminToken = createInstance('/admin/coupon');
export const versionWithAdminToken = createInstance('/admin/version');
export const appWithAdminToken = createInstance('/admin/app');
export const appImageWithAdminToken = createImageInstance('/v2/backoffice/admin/app');
export const communityWithAdminToken = createInstance('/admin/community');
export const merchantWithAdminToken = createInstance('/admin/merchant');
export const paymentWithAdminToken = createInstance('/admin/payment');
export const ticketWithAdminToken = createInstance('/admin/ticket');
export const franchiseWithAdminToken = createInstance('/admin/franchise');
export const salesWithAdminToken = createInstance('/admin/sales');
export const pgConfigWithAdminToken = createInstance('/admin/pg-config');

export const userWithUserToken = createInstance('/user');
export const merchantWithMerchantToken = createInstance('/merchant');
export const merchantImageWithMerchantToken = createImageInstance('/v2/backoffice/merchant');

export const seatWithMerchantToken = createInstance('/seat');
export const seatImageWithMerchantToken = createImageInstance('/v2/backoffice/seat');
export const userWithMerchantToken = createInstance('/user');
export const paymentWithMerchantToken = createInstance('/payment');
export const couponWithMerchantToken = createInstance('/coupon');
export const merchantPgWithMerchantToken = createInstance('/merchant-pg');
export const messageWithMerchantToken = createImageInstance('/v2/backoffice/message');
export const communityWithMerchantToken = createInstance('/community');
export const appWithMerchantToken = createInstance('/app');
export const appImageWithMerchantToken = createImageInstance('/v2/backoffice/app');
export const mileageWithMerchantToken = createInstance('/mileage');
export const logWithMerchantToken = createInstance('/log');

const instancesWithRefreshToken: AxiosInstance[] = [authWithRefreshToken, apiWithRefreshToken];
const instancesWithMerchantToken: AxiosInstance[] = [
  seat,
  user,
  ticket,
  apiWithMerchantToken,
  apiWithMerchantTokenV2,
  imageApiWithMerchantToken,
  userWithMerchantToken,
  paymentWithMerchantToken,
  seatWithMerchantToken,
  merchantWithMerchantToken,
  merchantPgWithMerchantToken,
  couponWithMerchantToken,
  messageWithMerchantToken,
  seatImageWithMerchantToken,
  authWithMerchantToken,
  merchantImageWithMerchantToken,
  communityWithMerchantToken,
  mileageWithMerchantToken,
  appWithMerchantToken,
  appImageWithMerchantToken,
  logWithMerchantToken,
];
const instancesWithUserToken: AxiosInstance[] = [userWithUserToken, authWithUserToken];
const instancesWithAdminToken: AxiosInstance[] = [
  authWithAdminToken,
  apiWithAdminToken,
  imageApiWithAdminToken,
  couponWithAdminToken,
  versionWithAdminToken,
  communityWithAdminToken,
  merchantWithAdminToken,
  userWithAdminToken,
  paymentWithAdminToken,
  ticketWithAdminToken,
  appWithAdminToken,
  appImageWithAdminToken,
  franchiseWithAdminToken,
  salesWithAdminToken,
  pgConfigWithAdminToken,
];

instancesWithRefreshToken.forEach((instance) => {
  instance.interceptors.request.use(withRefreshTokenAccepted, withTokenError);
  instance.interceptors.response.use(apply, withTokenRejected);
});
instancesWithMerchantToken.forEach((instance) => {
  instance.interceptors.request.use(withMerchantTokenAccepted, withTokenError);
  instance.interceptors.response.use(apply, withTokenRejected);
});
instancesWithUserToken.forEach((instance) => {
  instance.interceptors.request.use(withUserTokenAccepted, withTokenError);
  instance.interceptors.response.use(apply, withTokenRejected);
});
instancesWithAdminToken.forEach((instance) => {
  instance.interceptors.request.use(withAdminTokenAccepted, withTokenError);
  instance.interceptors.response.use(apply, withTokenRejected);
});

export { auth, authWithAdminToken, authWithRefreshToken, seat, ticket, user };

/**
 * 회원 조회
 * @param {*} merchantId
 * @param {*} data : { phoneNum?: string; name?: string; }
 */
export const getUserList = (merchantId: number, data: any) => {
  const phoneQuery = data.phoneNum ? `phoneNum=${data.phoneNum}` : '';
  const nameQuery = data.name ? `${data.phoneNum ? '&' : ''}name=${data.name}` : '';

  return user.get(`/userList/${merchantId}?${phoneQuery}${nameQuery}`);
};

/**
 * 회원 정보 조회
 * @param {*} merchantId
 * @param {*} userId
 */
export const userTicketList = (merchantId: number, userId: number, type: any) =>
  ticket.get(`/userTicketList/${merchantId}/${userId}/${type}`);

/**
 * 해당 좌석에 입장
 * @param {*} data : { merchantId: string; userId: string; ticketId: string; seatId: string; }
 * @returns
 */
export const forceEnter = (data: any) => ticket.patch(`/forceEnter`, data);
